import { ThemeEvents } from '@theme/events';

const SUBSCRIBED_VARIANTS_STORAGE_KEY = 'back-in-stock:subscribed-variants';

/**
 * "Notify me" sign-up for sold-out variants that qualify for back in stock.
 *
 * The form is rendered in Liquid (snippets/back-in-stock-form.liquid). The product
 * page re-renders it when the details morph on a variant switch, but quick add,
 * shop the look and featured product information only swap the add to cart
 * button. This element refreshes itself from the variant:update HTML so the form
 * follows the selected variant everywhere.
 *
 * Sign-ups are only sent when the Liquid wrapper carries data-signup-url, which
 * a theme setting controls. Without it, subscribe() is a mock that sends nothing.
 *
 * Signed-up variant IDs are kept in sessionStorage (no email), so the success
 * state survives a reload or switching variants and back within the tab.
 */
class BackInStockForm extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  #lastProcessedSequence = 0;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const variantUpdateTarget = this.closest('.shopify-section, dialog');
    variantUpdateTarget?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    // Listen on the element, not the form: the form is replaced on every variant switch
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.#showSubscribedStateIfSaved();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * @param {import('./events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const sequenceNumber = event.detail?.sequenceNumber || 0;
    if (sequenceNumber < this.#lastProcessedSequence) return;
    this.#lastProcessedSequence = sequenceNumber;

    const { html, productId, newProduct } = event.detail.data;
    if (newProduct) {
      this.dataset.productId = newProduct.id;
    } else if (productId !== this.dataset.productId) {
      return;
    }

    const updatedForm = html?.querySelector(`back-in-stock-form[data-product-id="${this.dataset.productId}"]`);
    const updatedMarkup = updatedForm ? updatedForm.innerHTML : '';
    if (this.innerHTML !== updatedMarkup) {
      this.innerHTML = updatedMarkup;
    }
    // Also needed when the product page morph already replaced the markup
    this.#showSubscribedStateIfSaved();
  };

  #showSubscribedStateIfSaved() {
    const form = this.querySelector('.back-in-stock__form');
    if (!(form instanceof HTMLFormElement)) return;
    const variantId = form.dataset.variantId;
    if (variantId && getSubscribedVariantIds().includes(variantId)) {
      showSubscribedState(form);
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('back-in-stock__form')) return;
    event.preventDefault();

    const emailInput = form.elements.namedItem('email');
    const submitButton = form.querySelector('.back-in-stock__submit');
    const errorMessage = form.querySelector('.back-in-stock__error');
    if (!(emailInput instanceof HTMLInputElement) || !(submitButton instanceof HTMLButtonElement)) return;
    if (submitButton.disabled) return;

    const submitLabel = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = submitButton.dataset.sendingLabel || submitLabel;
    errorMessage?.setAttribute('hidden', '');

    try {
      const variantId = form.dataset.variantId || '';
      await subscribe(emailInput.value.trim(), variantId, this.dataset);
      saveSubscribedVariantId(variantId);
      showSubscribedState(form);
    } catch {
      errorMessage?.removeAttribute('hidden');
      emailInput.focus();
    } finally {
      submitButton.disabled = false;
      submitButton.textContent = submitLabel;
    }
  };
}

/**
 * @param {HTMLFormElement} form
 */
function showSubscribedState(form) {
  form.classList.add('back-in-stock__form--subscribed');
  form.querySelector('.back-in-stock__success')?.removeAttribute('hidden');
}

/**
 * @returns {string[]}
 */
function getSubscribedVariantIds() {
  try {
    const savedVariantIds = JSON.parse(sessionStorage.getItem(SUBSCRIBED_VARIANTS_STORAGE_KEY) || '[]');
    return Array.isArray(savedVariantIds) ? savedVariantIds : [];
  } catch {
    return [];
  }
}

/**
 * @param {string} variantId
 */
function saveSubscribedVariantId(variantId) {
  if (!variantId) return;
  const subscribedVariantIds = getSubscribedVariantIds();
  if (subscribedVariantIds.includes(variantId)) return;
  try {
    sessionStorage.setItem(SUBSCRIBED_VARIANTS_STORAGE_KEY, JSON.stringify([...subscribedVariantIds, variantId]));
  } catch {
    // Storage blocked (private mode): the success state just won't persist
  }
}

const SIGNUP_API_REVISION = '2026-07-15';

/**
 * Sends the sign-up when the theme setting is on, otherwise runs the mock.
 * @param {string} email
 * @param {string} variantId
 * @param {DOMStringMap} config - data-signup-url and data-catalog-variant-prefix from the wrapper
 * @returns {Promise<void>}
 */
async function subscribe(email, variantId, config) {
  const { signupUrl, catalogVariantPrefix = '' } = config;
  if (!signupUrl) return mockSubscribe(email, variantId);

  const response = await fetch(signupUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/vnd.api+json',
      revision: SIGNUP_API_REVISION,
    },
    body: JSON.stringify({
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email } } },
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: `${catalogVariantPrefix}${variantId}` } },
        },
      },
    }),
  });
  if (!response.ok) throw new Error(`Sign-up failed with status ${response.status}`);
}

/**
 * Mock sign-up: sends nothing, logs to the console and resolves after a short
 * delay. Add ?bis_mock=error to the page URL to see the error state.
 * @param {string} email
 * @param {string} variantId
 * @returns {Promise<void>}
 */
function mockSubscribe(email, variantId) {
  console.info('[back-in-stock] Mock sign-up, nothing was sent.', { email, variantId });

  const shouldFail = new URLSearchParams(window.location.search).get('bis_mock') === 'error';
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (shouldFail) {
        reject(new Error('Mock error'));
      } else {
        resolve();
      }
    }, 600);
  });
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}
